import { useEffect, useRef } from 'react';
import { useAuth } from '@Auth/useAuth';

declare global {
  interface Window {
    Tawk_API?: TawkAPI;
  }
}

interface TawkAPI {
  start: (options?: { showWidget?: boolean }) => void;
  shutdown: () => void;
  showWidget: () => void;
  hideWidget: () => void;
  onLoad: (() => void) | undefined;
  login: (credentials: { hash: string; userId: string }) => void;
  toggle: () => void;
  setAttributes: (
    attributes: Record<string, string>,
    callback: (error?: unknown) => void
  ) => void;
}

// Tawk's start() has an internal init race: even once start is a function it
// can throw "$_Tawk.i18next is not a function" because its own localization
// bundle hasn't finished loading. We can't observe that internal state, so we
// retry on a short bounded backoff instead of letting it crash the app.
const START_RETRY_DELAY_MS = 300;
const START_MAX_ATTEMPTS = 10;

export function useTawkTo() {
  const { isAuthenticated, baseUser } = useAuth();
  const isStarted = useRef(false);

  useEffect(() => {
    const tawk = window.Tawk_API;
    if (!tawk) return;

    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    if (isAuthenticated && baseUser && !isStarted.current) {
      const applyAttributes = () => {
        tawk.hideWidget();
        tawk.setAttributes(
          {
            name: baseUser.name || '',
            email: baseUser.email || '',
          },
          (error) => {
            if (error) console.error('Tawk.to setAttributes error:', error);
          }
        );
      };

      const start = (attempt: number) => {
        if (typeof tawk.start !== 'function') return;
        try {
          tawk.start({ showWidget: false });
        } catch (error) {
          // SDK loaded but its internals aren't ready yet — back off and retry.
          if (attempt < START_MAX_ATTEMPTS) {
            isStarted.current = false;
            retryTimer = setTimeout(() => start(attempt + 1), START_RETRY_DELAY_MS);
          } else {
            console.error('Tawk.to failed to start:', error);
          }
          return;
        }
        if (typeof tawk.setAttributes === 'function') {
          applyAttributes();
        } else {
          tawk.onLoad = applyAttributes;
        }
      };

      // The inline embed in index.html creates Tawk_API synchronously but the
      // real SDK loads async — methods are undefined until then. If we call
      // before the SDK is ready (slow CDN, blocked by ad-blocker, offline),
      // we'd crash the app. Defer to onLoad when needed.
      if (typeof tawk.start === 'function') {
        isStarted.current = true;
        start(0);
      } else {
        tawk.onLoad = () => {
          isStarted.current = true;
          start(0);
        };
      }
    }

    if (!isAuthenticated && isStarted.current) {
      isStarted.current = false;
      if (typeof tawk.shutdown === 'function') tawk.shutdown();
    }

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isAuthenticated, baseUser]);
}
